import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ErrorState as UiErrorState,
  type ErrorStateProps as UiErrorStateProps,
} from "@/components/ui/ErrorState";
import { sanitizeUserFacingErrorText } from "@/lib/errorMessages";

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  action?: ReactNode;
  className?: string;
}

/** App error placeholder with i18n defaults and sanitized API messages. */
export function ErrorState({
  title,
  message,
  onRetry,
  isRetrying = false,
  action,
  className,
}: ErrorStateProps) {
  const { t } = useTranslation("common");
  const resolvedTitle = title ?? t("errorOccurred");
  const resolvedMessage = message ?? t("somethingWentWrong");
  const safeMessage = sanitizeUserFacingErrorText(
    resolvedMessage,
    t("somethingWentWrong"),
  );

  const uiProps: UiErrorStateProps = {
    title: resolvedTitle,
    message: safeMessage ?? resolvedMessage,
    onRetry,
    retryLabel: onRetry ? t("tryAgain") : undefined,
    isRetrying,
    action,
    className,
  };

  return <UiErrorState {...uiProps} />;
}
